/**
 * sessionGuard.ts — 로그인 토큰이 끝났을 때 막다른 화면 대신 알맞은 곳으로 (2026-10-28 지도사 화면: 「오재인 계정으로 쓰는 중」 띠 + 「세션이 만료되었습니다 · 다시 시도」)
 *  원인: 직원 계정으로 전환해 쓰는 토큰은 12시간 — 지나면 모든 요청이 401 인데 화면은 「다시 시도」(또 401)만 보였고,
 *        「임우승으로 돌아가기」도 끝난 토큰으로 서버에 부탁해 못 돌아갔다.
 *  고침: 전환할 때 원래(내) 토큰을 따로 맡겨 두고 —
 *   · 전환 토큰이 끝났으면 → 맡겨 둔 내 토큰(아직 살아 있으면)으로 조용히 돌아온다
 *   · 내 토큰도 끝났으면 → 로그인 화면으로(저장 지움)
 *  화면을 열 때(index) 한 번 · 요청이 「만료 · 전환 끝」 401 을 받으면 한 번 확인한다.
 */
const TOKEN = 'token';
const ACCOUNT = 'account';
const ORIGIN_TOKEN = 'token_origin';
const ORIGIN_ACCOUNT = 'account_origin';

interface Claims { id: string; acting_by: string; exp: number }

export function claimsOf(tok: string | null): Claims | null {
  if (!tok) return null;
  try {
    const p = tok.split('.')[1] || '';
    const j = JSON.parse(decodeURIComponent(escape(atob(p.replace(/-/g, '+').replace(/_/g, '/'))))) as { id?: string; acting_by?: string; exp?: number };
    return { id: String(j.id || ''), acting_by: String(j.acting_by || ''), exp: Number(j.exp) || 0 };
  } catch (e) { return null; }
}
/** 끝났거나 1분 안에 끝남(시계 오차) */
const dead = (c: Claims | null, now: number) => !c || (c.exp > 0 && c.exp * 1000 < now + 60000);
const get = (k: string) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
const put = (k: string, v: string | null) => { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch (e) { /* 저장 못 함 */ } };

/** 전환하기 직전 — 지금이 내 계정(전환 아님)이면 내 토큰을 맡겨 둔다 · 내 계정으로 돌아오면 지운다 */
export function rememberOrigin(nextToken: string): void {
  const cur = get(TOKEN);
  const c = claimsOf(cur), n = claimsOf(nextToken);
  if (n && !n.acting_by) { put(ORIGIN_TOKEN, null); put(ORIGIN_ACCOUNT, null); return; }   // 돌아옴
  if (c && !c.acting_by && cur) { put(ORIGIN_TOKEN, cur); put(ORIGIN_ACCOUNT, get(ACCOUNT)); }
}
/** 맡겨 둔 내 토큰 — 살아 있고 · 지금 전환 토큰의 원래 계정과 같을 때만 */
function originFor(actingBy: string, now: number): string | null {
  const o = get(ORIGIN_TOKEN);
  const c = claimsOf(o);
  return o && c && !c.acting_by && c.id === actingBy && !dead(c, now) ? o : null;
}
/** 맡겨 둔 내 계정으로 바로 돌아가기(서버 없이) — 됐으면 true */
export function backToOrigin(now = Date.now()): boolean {
  const c = claimsOf(get(TOKEN));
  if (!c || !c.acting_by) return false;
  const o = originFor(c.acting_by, now);
  if (!o) return false;
  put(TOKEN, o); put(ACCOUNT, get(ORIGIN_ACCOUNT)); put(ORIGIN_TOKEN, null); put(ORIGIN_ACCOUNT, null);
  return true;
}
export function clearSession(): void { [TOKEN, ACCOUNT, ORIGIN_TOKEN, ORIGIN_ACCOUNT].forEach((k) => put(k, null)); }

/**
 * 지금 토큰 정리 — 'ok'(그대로) · 'origin'(내 계정으로 돌아옴) · 'login'(로그인부터)
 *  토큰이 없으면 'ok'(로그인 화면은 원래대로 뜸)
 */
export function settleSession(now = Date.now()): 'ok' | 'origin' | 'login' {
  const tok = get(TOKEN);
  if (!tok) return 'ok';
  const c = claimsOf(tok);
  if (!dead(c, now)) return 'ok';
  if (c && c.acting_by && backToOrigin(now)) return 'origin';
  clearSession();
  return 'login';
}

/** 요청이 401 을 받았을 때 — 토큰이 정말 끝난 경우만 정리하고 처음 화면을 다시 연다(한 번만) */
let handled = false;
export function onAuthLost(message: string, now = Date.now()): void {
  if (handled) return;
  const c = claimsOf(get(TOKEN));
  const ended = /만료|전환이 끝났/.test(String(message || '')) || dead(c, now);
  if (!ended) return;
  let r = settleSession(now);
  if (r === 'ok' && c && c.acting_by) r = backToOrigin(now) ? 'origin' : (clearSession(), 'login');   // 서버가 전환을 끝냄(권한 빠짐 등)
  if (r === 'ok') return;
  handled = true;
  try { window.location.assign('/'); } catch (e) { /* 시험 환경 */ }
}
